/**
 * Settings > Case Preparation > Filing Packet > Include In New Packets: the
 * firm's work product kinds, each checked or not, dragged into the order a
 * new packet lists and prints them. The exhibits stay last: the build always
 * prints them after the work products.
 */
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type Announcements,
  type DragEndEvent,
  type UniqueIdentifier
} from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical } from "lucide-react";
import type { FC } from "react";

import { Checkbox } from "@/components/ui/checkbox";
import { Field, FieldLabel } from "@/components/ui/field";
import {
  PACKET_CONTENT_LABELS,
  type PacketContentKind,
  type PacketContents
} from "@/features/caseIde/workflows/packetContents";
import { cn } from "@/lib/utils";

type RowProps = {
  kind: PacketContentKind;
  on: boolean;
  disabled: boolean;
  onPick: (on: boolean) => void;
};

const Box: FC<RowProps> = ({ kind, on, disabled, onPick }) => (
  <>
    <Checkbox
      id={`packet-style-contents-${kind}`}
      checked={on}
      disabled={disabled}
      onCheckedChange={(v) => onPick(v === true)}
      data-testid={`packet-style-contents-${kind}`}
    />
    <FieldLabel
      htmlFor={`packet-style-contents-${kind}`}
      className="text-sm font-normal text-fg-2"
    >
      {PACKET_CONTENT_LABELS[kind]}
    </FieldLabel>
  </>
);

const SortableRow: FC<RowProps> = ({ kind, on, disabled, onPick }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: kind, disabled });
  return (
    <Field
      ref={setNodeRef}
      orientation="horizontal"
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn("rounded-sm", isDragging && "z-10 bg-elevated shadow-sm")}
      data-testid={`packet-style-order-${kind}`}
    >
      <button
        type="button"
        className="inline-flex shrink-0 cursor-grab touch-none text-fg-3 hover:text-fg-2 active:cursor-grabbing disabled:cursor-not-allowed disabled:opacity-50"
        aria-label={`Reorder ${PACKET_CONTENT_LABELS[kind]}`}
        disabled={disabled}
        {...attributes}
        {...listeners}
        data-testid={`packet-style-order-handle-${kind}`}
      >
        <GripVertical className="size-3.5" />
      </button>
      <Box kind={kind} on={on} disabled={disabled} onPick={onPick} />
    </Field>
  );
};

const labelOf = (id: UniqueIdentifier) =>
  PACKET_CONTENT_LABELS[id as PacketContentKind] ?? String(id);

type Props = {
  /** Every kind, the exhibits last. */
  order: readonly PacketContentKind[];
  contents: PacketContents;
  disabled: boolean;
  onPick: (kind: PacketContentKind, on: boolean) => void;
  onReorder: (order: PacketContentKind[]) => void;
};

export const PacketContentsList: FC<Props> = ({
  order,
  contents,
  disabled,
  onPick,
  onReorder
}) => {
  const movable: PacketContentKind[] = order.filter((k) => k !== "exhibits");
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  );
  const place = (id: UniqueIdentifier) =>
    `position ${movable.indexOf(id as PacketContentKind) + 1} of ${
      movable.length
    }`;
  const announcements: Announcements = {
    onDragStart: ({ active }) => `Picked up ${labelOf(active.id)}.`,
    onDragOver: ({ active, over }) =>
      over ? `${labelOf(active.id)} is at ${place(over.id)}.` : undefined,
    onDragEnd: ({ active, over }) =>
      over
        ? `${labelOf(active.id)} dropped at ${place(over.id)}.`
        : `${labelOf(active.id)} dropped.`,
    onDragCancel: ({ active }) => `Moving ${labelOf(active.id)} was cancelled.`
  };

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    const from = movable.indexOf(active.id as PacketContentKind);
    const to = movable.indexOf(over.id as PacketContentKind);
    if (from < 0 || to < 0) return;
    onReorder([...arrayMove(movable, from, to), "exhibits"]);
  };

  return (
    <fieldset
      aria-label="Include In New Packets"
      className="flex flex-col gap-1.5"
      data-testid="packet-style-contents"
    >
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        modifiers={[restrictToVerticalAxis]}
        onDragEnd={onDragEnd}
        accessibility={{
          announcements,
          screenReaderInstructions: {
            draggable:
              "To reorder, press Space or Enter, move with the arrow keys, then press Space or Enter again to drop. Press Escape to cancel."
          }
        }}
      >
        <SortableContext items={movable} strategy={verticalListSortingStrategy}>
          {movable.map((kind) => (
            <SortableRow
              key={kind}
              kind={kind}
              on={contents[kind]}
              disabled={disabled}
              onPick={(on) => onPick(kind, on)}
            />
          ))}
        </SortableContext>
      </DndContext>
      <Field orientation="horizontal" data-testid="packet-style-order-exhibits">
        <span className="inline-flex size-3.5 shrink-0" aria-hidden />
        <Box
          kind="exhibits"
          on={contents.exhibits}
          disabled={disabled}
          onPick={(on) => onPick("exhibits", on)}
        />
        <span className="ml-auto shrink-0 text-sm text-fg-3">Always Last</span>
      </Field>
    </fieldset>
  );
};
